import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState, useMemo, Fragment } from 'react';
import { isSuperAdmin } from '@/utils/auth';
import { useAuditLogQuery, type GetAuditLogParams } from '@/api/audit-log/getAuditLog';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { Pagination } from '@/components/data/Pagination/Pagination';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const Route = createFileRoute('/dashboard/audit-log/')({
  beforeLoad: () => {
    if (!isSuperAdmin()) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: AuditLogPage,
});

function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<GetAuditLogParams>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const params: GetAuditLogParams = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...filters,
  };

  const { data, isLoading } = useAuditLogQuery(params);
  const { data: tenants } = useGetTenantsQuery();
  const tenantNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of tenants ?? []) {
      map[t.tenant_id] = t.name_en || t.title || t.tenant_id;
    }
    return map;
  }, [tenants]);
  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;

  const ALL = '__all__';

  const handleFilterChange = (key: keyof GetAuditLogParams, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value === ALL ? undefined : value || undefined }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <PageContainer>
      <Breadcrumbs overrides={{ 'audit-log': 'Audit Log' }} />

      <div className="flex gap-4 items-center mb-6">
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <span className="text-sm text-gray-500 ml-auto">{total} entries</span>
      </div>

      {/* Filters */}
      <ContentCard className="mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="grid gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label>Action</Label>
                </TooltipTrigger>
                <TooltipContent>
                  Filter by action (e.g. pickem.create, daily_reveal.days.create)
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Input
              placeholder="Filter by action..."
              value={filters.action ?? ''}
              onChange={e => handleFilterChange('action', e.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label>Entity Type</Label>
            <Select
              value={filters.entityType ?? ALL}
              onValueChange={v => handleFilterChange('entityType', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All types</SelectItem>
                {[
                  'pickem',
                  'question',
                  'admin_user',
                  'tenant',
                  'daily_reveal',
                  'squares',
                  'lobby',
                  'prize_payout',
                  'payout_template',
                  'game_rules',
                  'leaderboard',
                  'live_scoring',
                  'settings',
                  'grading',
                  'upload',
                  'google_ads',
                ].map(t => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label>Admin Email</Label>
                </TooltipTrigger>
                <TooltipContent>Filter logs by admin email (partial match)</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Input
              placeholder="Filter by email..."
              value={filters.adminEmail ?? ''}
              onChange={e => handleFilterChange('adminEmail', e.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label>Tenant</Label>
            <Select
              value={filters.tenantId ?? ALL}
              onValueChange={v => handleFilterChange('tenantId', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All tenants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All tenants</SelectItem>
                {(tenants ?? []).map(t => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id}>
                    {t.name_en || t.title || t.tenant_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>From Date</Label>
            <Input
              type="date"
              value={filters.fromDate ?? ''}
              onChange={e => handleFilterChange('fromDate', e.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label>To Date</Label>
            <Input
              type="date"
              value={filters.toDate ?? ''}
              onChange={e => handleFilterChange('toDate', e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reset all filter fields</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </ContentCard>

      {/* Table */}
      <ContentCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">Timestamp</TableHead>
              <TableHead className="whitespace-nowrap">Admin</TableHead>
              <TableHead className="whitespace-nowrap">Action</TableHead>
              <TableHead className="whitespace-nowrap">Entity Type</TableHead>
              <TableHead className="whitespace-nowrap">Entity</TableHead>
              <TableHead className="whitespace-nowrap">Tenant</TableHead>
              <TableHead className="whitespace-nowrap">Changes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                  No audit log entries found
                </TableCell>
              </TableRow>
            ) : (
              logs.map(log => (
                <Fragment key={log.id}>
                  <TableRow
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-accent"
                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  >
                    <TableCell className="text-sm whitespace-nowrap">
                      <TzDateDisplay
                        utcDate={log.createdAt}
                        formatString="MMM d, yyyy h:mm:ss a"
                        showUtcBadge={false}
                        showTimezoneTooltip={false}
                        dateOnly={false}
                      />
                    </TableCell>
                    <TableCell className="text-sm">{log.adminUserEmail}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{log.entityType}</TableCell>
                    <TableCell className="text-sm">
                      <span className="font-mono text-xs text-gray-500">{log.entityId}</span>
                      {log.entityLabel && (
                        <span className="ml-2 text-gray-700 dark:text-gray-300">
                          {log.entityLabel}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {log.tenantId ? (tenantNameMap[log.tenantId] ?? log.tenantId) : '\u2014'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-400">
                      {log.changes ? 'View' : '\u2014'}
                    </TableCell>
                  </TableRow>
                  {expandedId === log.id && log.changes ? (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-gray-50 dark:bg-accent p-4">
                        <pre className="text-xs overflow-auto max-h-48 text-gray-700 dark:text-gray-300">
                          {JSON.stringify(log.changes, null, 2)}
                        </pre>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>

        <Pagination
          total={total}
          pageSize={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </ContentCard>
    </PageContainer>
  );
}
